import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { AlertTriangle, Lock, ShieldAlert, Users } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useT } from '@/i18n'
import { Dialog } from './ui/dialog'
import { Button } from './ui/button'
import { Input, Textarea } from './ui/input'
import { Skeleton } from './States'
import { PlannedBadge } from './DataDisplay'

/**
 * X2 — XAVFLI AMALLAR PATTERNI (5 daraja).
 * Manba: TZ §X2 + skill references/danger-actions.md
 *
 *   1 Yengil     — oddiy dialog, undo toast
 *   2 O'rtacha   — + sabab majburiy (min 10 belgi)
 *   3 Og'ir      — + ta'sir hisoboti + obyekt nomini QO'LDA yozish
 *   4 Kritik     — four-eyes (ikkinchi xodim tasdig'i)
 *   5 Favqulodda — four-eyes + 10 daqiqa kutish + jamoaga xabar
 *
 * ⚠️ 4–5 DARAJALAR TO'LIQ ISHLAMAYDI. Four-eyes ning MA'LUMOT MODELI TZ da
 * yo'q (PROGRESS.md §4.2: «17 marta eslatiladi, 0 ta SQL bloki»). Oqim UI'si
 * quriladi, lekin so'rov yaratish `onCreateRequest` propisiz — sxema
 * tasdiqlanmaguncha ataylab bajarilmaydi. Taklif: tz/admin/HOLAT.md §3.
 */

export type DangerLevel = 1 | 2 | 3 | 4 | 5

export interface ImpactItem {
  label: string
  value: string | number
  /** Diqqat tortishi kerak bo'lgan qator (masalan pul harakati). */
  emphasis?: boolean
}

export interface ImpactData {
  items: ImpactItem[]
  /** Tiklash oynasi ("7 kun") yoki `false` — qaytarib bo'lmaydi. */
  reversible: string | false
}

const MIN_REASON = 10
const MIN_REASON_L5 = 50 // break-glass darajasi — rbac-ui.md
const ARM_DELAY_MS = 500 // dialog ochilgach tugma o'chiq turadi
const WAIT_PERIOD_MIN = 10 // 5-daraja kutish davri

const LEVEL_TONE: Record<DangerLevel, { icon: ReactNode; ring: string }> = {
  1: { icon: <AlertTriangle size={16} className="text-secondary" />, ring: 'border-border-default' },
  2: { icon: <AlertTriangle size={16} className="text-warning" />, ring: 'border-warning' },
  3: { icon: <AlertTriangle size={16} className="text-danger" />, ring: 'border-danger' },
  4: { icon: <Lock size={16} className="text-danger" />, ring: 'border-danger' },
  5: { icon: <ShieldAlert size={16} className="text-critical" />, ring: 'border-critical' },
}

/* ═══ Ta'sir hisoboti ═══════════════════════════════════════════
   danger-actions.md: hisobot HISOBLANADI, taxmin qilinmaydi.
   3 soniyadan uzoq bo'lsa — skeleton, tugma o'chiq. */

export function ImpactReport({ data, loading }: { data?: ImpactData; loading?: boolean }) {
  const t = useT()

  if (loading || !data) {
    return (
      <div className="rounded-md border border-border-default bg-surface-1 p-3">
        <p className="mb-2 text-sm font-medium text-secondary">{t.danger.impactCalculating}</p>
        <div className="space-y-1.5">
          <Skeleton className="h-3 w-3/5" />
          <Skeleton className="h-3 w-2/5" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-md border border-border-default bg-surface-1 p-3">
      <p className="mb-2 text-sm font-medium text-secondary">{t.danger.impactTitle}</p>
      <ul className="space-y-1">
        {data.items.map((item, i) => (
          <li key={i} className="flex items-baseline gap-2 text-base">
            <span className="text-tertiary" aria-hidden>
              ·
            </span>
            <span className={cn('font-mono tabular', item.emphasis ? 'text-warning' : 'text-primary')}>
              {typeof item.value === 'number' ? item.value.toLocaleString() : item.value}
            </span>
            <span className="text-secondary">{item.label}</span>
          </li>
        ))}
      </ul>
      <p
        className={cn(
          'mt-2.5 flex items-center gap-1.5 border-t border-border-subtle pt-2 text-sm',
          data.reversible ? 'text-secondary' : 'text-danger',
        )}
      >
        <span aria-hidden>{data.reversible ? '↩' : '⚠'}</span>
        {data.reversible ? t.danger.reversible(data.reversible) : t.danger.irreversible}
      </p>
    </div>
  )
}

/* ═══ Sabab maydoni ═════════════════════════════════════════════
   components.md `<ReasonField>`: dropdown (tez sabablar) + erkin matn. */

export function ReasonField({
  value,
  onChange,
  presets,
  minChars = MIN_REASON,
  autoFocus,
}: {
  value: string
  onChange: (v: string) => void
  presets?: string[]
  minChars?: number
  autoFocus?: boolean
}) {
  const t = useT()
  const tooShort = value.trim().length > 0 && value.trim().length < minChars

  return (
    <div className="space-y-1.5">
      <label htmlFor="danger-reason" className="block text-sm font-medium text-secondary">
        {t.danger.reasonRequired}
      </label>

      {presets && presets.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {presets.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => onChange(p)}
              className="rounded-sm border border-border-default px-1.5 py-0.5 text-xs text-secondary hover:border-border-strong hover:text-primary"
            >
              {p}
            </button>
          ))}
        </div>
      )}

      <Textarea
        id="danger-reason"
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t.danger.reasonPlaceholder}
        aria-invalid={tooShort}
        rows={3}
      />
      <p className={cn('text-xs', tooShort ? 'text-danger' : 'text-tertiary')}>
        {t.danger.reasonTooShort(minChars)} · {value.trim().length}/{minChars}
      </p>
    </div>
  )
}

/* ═══ DangerDialog ══════════════════════════════════════════════ */

export interface DangerDialogProps {
  open: boolean
  onClose: () => void
  /** TZ ekran ID'si — audit uchun. */
  screenId: string
  level: DangerLevel
  title: string
  description?: string
  confirmLabel: string
  /** 3-daraja: foydalanuvchi AYNAN shu matnni yozishi kerak. */
  objectName?: string
  reasonPresets?: string[]
  /** Statik hisobot yoki hisoblovchi funksiya (danger-actions.md). */
  impact?: ImpactData | (() => Promise<ImpactData>)
  /** 1–3 darajalar: amalni bajaradi. */
  onConfirm?: (payload: { reason: string }) => Promise<void> | void
  /**
   * 4–5 darajalar: four-eyes SO'ROVINI yaratadi (amalni bajarmaydi).
   * Berilmasa — oqim ko'rsatiladi, lekin «rejalashtirilgan» deb belgilanadi.
   */
  onCreateRequest?: (payload: { reason: string }) => Promise<void> | void
}

export function DangerDialog({
  open,
  onClose,
  screenId,
  level,
  title,
  description,
  confirmLabel,
  objectName,
  reasonPresets,
  impact,
  onConfirm,
  onCreateRequest,
}: DangerDialogProps) {
  const t = useT()
  const [reason, setReason] = useState('')
  const [typed, setTyped] = useState('')
  const [armed, setArmed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [impactData, setImpactData] = useState<ImpactData | undefined>(
    typeof impact === 'function' ? undefined : impact,
  )
  const [impactLoading, setImpactLoading] = useState(false)
  const cancelRef = useRef<HTMLButtonElement>(null)

  const minChars = level >= 5 ? MIN_REASON_L5 : MIN_REASON

  // Har ochilishda toza holat + 500 ms «qurollanish» kechikishi
  // (tasodifiy ikki marta bosishdan himoya — danger-actions.md).
  useEffect(() => {
    if (!open) return
    setReason('')
    setTyped('')
    setArmed(false)
    setBusy(false)
    const h = window.setTimeout(() => setArmed(true), ARM_DELAY_MS)
    return () => window.clearTimeout(h)
  }, [open])

  // Ta'sir hisobotini hisoblash.
  useEffect(() => {
    if (!open || typeof impact !== 'function') return
    let alive = true
    setImpactLoading(true)
    Promise.resolve(impact())
      .then((d) => {
        if (alive) setImpactData(d)
      })
      .finally(() => {
        if (alive) setImpactLoading(false)
      })
    return () => {
      alive = false
    }
  }, [open, impact])

  const needsReason = level >= 2
  const needsTyping = level >= 3 && Boolean(objectName)
  const isFourEyes = level >= 4

  const canSubmit = useMemo(() => {
    if (!armed || busy) return false
    if (impactLoading) return false
    if (needsReason && reason.trim().length < minChars) return false
    if (needsTyping && typed.trim() !== objectName) return false
    // Four-eyes: sxema tasdiqlanmagan → so'rov yaratuvchi berilmagan bo'lsa bloklanadi.
    if (isFourEyes && !onCreateRequest) return false
    if (!isFourEyes && !onConfirm) return false
    return true
  }, [
    armed, busy, impactLoading, needsReason, reason, minChars,
    needsTyping, typed, objectName, isFourEyes, onCreateRequest, onConfirm,
  ])

  const submit = useCallback(async () => {
    if (!canSubmit) return
    setBusy(true)
    try {
      if (isFourEyes) await onCreateRequest?.({ reason: reason.trim() })
      else await onConfirm?.({ reason: reason.trim() })
      onClose()
    } finally {
      setBusy(false)
    }
  }, [canSubmit, isFourEyes, onCreateRequest, onConfirm, reason, onClose])

  const tone = LEVEL_TONE[level]

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      {...(description ? { description } : {})}
      icon={tone.icon}
      width={level >= 3 ? 'md' : 'sm'}
      initialFocusRef={cancelRef}
      // Xavfli dialog tasodifan fon bosilganda yopilmaydi.
      closeOnOverlay={level < 3}
      footer={
        <>
          {/* danger-actions.md: buzuvchi tugma o'ng pastda EMAS —
              odat bo'yicha «OK» turadigan joyda turmaydi. */}
          <Button
            variant={level >= 5 ? 'critical' : level >= 3 ? 'danger' : level === 2 ? 'warning' : 'default'}
            disabled={!canSubmit}
            onClick={submit}
            data-screen={screenId}
          >
            {busy ? t.common.saving : isFourEyes ? t.danger.fourEyesCreate : confirmLabel}
          </Button>
          <div className="flex-1" />
          <Button ref={cancelRef} variant="default" onClick={onClose}>
            {t.common.cancel}
          </Button>
        </>
      }
    >
      <div className={cn('space-y-3 border-l-2 pl-3', tone.ring)}>
        <p className="text-xs font-medium tracking-wide text-tertiary uppercase">
          {t.danger.level(level)} — {t.danger.levelName[level]}
        </p>

        {(impact || impactLoading) && <ImpactReport {...(impactData ? { data: impactData } : {})} loading={impactLoading} />}

        {needsReason && (
          <ReasonField
            value={reason}
            onChange={setReason}
            {...(reasonPresets ? { presets: reasonPresets } : {})}
            minChars={minChars}
          />
        )}

        {needsTyping && objectName && (
          <div className="space-y-1.5">
            <label htmlFor="danger-confirm" className="block text-sm font-medium text-secondary">
              {t.danger.typeToConfirmLabel(objectName)}
            </label>
            <Input
              id="danger-confirm"
              mono
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              // Enter buzuvchi amalni BAJARMAYDI (danger-actions.md).
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.preventDefault()
              }}
              autoComplete="off"
              aria-invalid={typed.length > 0 && typed.trim() !== objectName}
              placeholder={objectName}
            />
            {typed.length > 0 && typed.trim() !== objectName && (
              <p className="text-xs text-danger">{t.danger.typeToConfirmMismatch}</p>
            )}
          </div>
        )}

        {isFourEyes && (
          <div className="space-y-2 rounded-md border border-danger bg-danger-subtle p-3">
            <p className="flex items-center gap-1.5 text-sm font-medium text-danger">
              <Users size={13} />
              {t.danger.fourEyesTitle}
            </p>
            <p className="text-sm text-secondary">{t.danger.fourEyesDesc}</p>

            {level >= 5 && (
              <>
                <p className="text-sm text-secondary">{t.danger.waitPeriodDesc(WAIT_PERIOD_MIN)}</p>
                <p className="text-sm text-warning">{t.danger.notifyTeam}</p>
              </>
            )}

            {/* Sxema tasdiqlanmaguncha bu oqim bajarilmaydi — yolg'on
                «ishlaydi» ko'rinishi berilmaydi. */}
            {!onCreateRequest && (
              <div className="flex items-center gap-2 pt-1">
                <PlannedBadge />
                <span className="text-xs text-tertiary">four_eyes_requests</span>
              </div>
            )}
          </div>
        )}
      </div>
    </Dialog>
  )
}
